/**
 * The dashboard's search box index, ported from the design (COL-533). Two
 * separate indexes — a tenant with a booking searches different things than
 * an applicant does — matched against the result's title in English, German
 * and the reader's language, plus a hand-written bag of DE/EN keywords.
 *
 * Deliberately static, not derived from live data: the entries describe
 * SCREENS ("Rent & payments" → My room · Payments), not individual rows, so
 * there is nothing to keep in sync with the database. A result opens a tab
 * the same way a `?tab=` deep link does, through `DashboardV2Tab` — never a
 * raw string the caller invents — and, when it has one, a `?section=` target
 * that scrolls to the block on that tab (COL-623). Before COL-623 a result
 * only switched the tab, so "Landlord confirmation" left the tenant at the
 * top of My room, and from My room itself did nothing visible at all.
 */

import type { DashboardV2Tab } from "@/lib/tenant-dashboard-tabs";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";

type DashboardV2Labels = ReturnType<typeof getTenantDashboardV2Labels>;

/** A label key of the dashboard copy that names a result or its location. */
export type SearchCopyKey = Extract<
  keyof DashboardV2Labels,
  `searchResult${string}` | `searchWhere${string}`
>;

/**
 * Where on a tab a result lands. Each value is a `data-search-target`
 * attribute on exactly one rendered block; the dashboard test asserts that,
 * so a target nothing renders fails CI rather than silently doing nothing.
 */
export const SEARCH_TARGETS = [
  "room-contract",
  "room-payments",
  "room-move",
  "room-info",
  "room-landlord-confirmation",
  "room-waste-sorting",
  "room-broadcasting-fee",
  "help-request",
  "help-faq",
  "applications-documents",
] as const;

export type SearchTarget = (typeof SEARCH_TARGETS)[number];

/** The URL parameter that carries a {@link SearchTarget}. */
export const SEARCH_TARGET_PARAM = "section";

export function isSearchTarget(value: string | null): value is SearchTarget {
  return (SEARCH_TARGETS as readonly (string | null)[]).includes(value);
}

export type SearchEntry = {
  title: SearchCopyKey;
  /** Breadcrumb shown under the title, e.g. "My room · Payments". */
  where: SearchCopyKey;
  tab: DashboardV2Tab;
  /** The block to scroll to. Absent when the whole tab is the answer. */
  target?: SearchTarget;
  /** DE+EN keywords, space-separated, lowercased at author time. */
  keywords: string;
};

/** A tenant with a booking. */
export const TENANT_SEARCH_INDEX: readonly SearchEntry[] = [
  {
    title: "searchResultHandoverReport",
    where: "searchWhereRoomMove",
    tab: "room",
    target: "room-move",
    keywords:
      "einzug einzugsprotokoll übergabeprotokoll protokoll übergabe fotos schlüssel move-in report handover photos key",
  },
  {
    title: "searchResultMoveOut",
    where: "searchWhereRoomMove",
    tab: "room",
    target: "room-move",
    keywords:
      "auszug auszugsprotokoll abnahme termin ende kündigung move-out inspection appointment end notice",
  },
  {
    title: "searchResultContractPdf",
    where: "searchWhereRoomContract",
    tab: "room",
    target: "room-contract",
    keywords:
      "vertrag mietvertrag pdf unterschrift laufzeit contract rental lease signature term",
  },
  {
    title: "searchResultRentPayments",
    where: "searchWhereRoomPayments",
    tab: "room",
    target: "room-payments",
    keywords:
      "miete zahlung zahlungen abbuchung beleg verlauf rent payment payments debit receipt history",
  },
  {
    title: "searchResultSepa",
    where: "searchWhereRoomPayments",
    tab: "room",
    target: "room-payments",
    keywords:
      "sepa lastschrift lastschriftmandat mandat iban konto direct debit mandate account bank",
  },
  {
    title: "searchResultDeposit",
    where: "searchWhereRoomPayments",
    tab: "room",
    target: "room-payments",
    keywords: "kaution rückzahlung deposit refund",
  },
  {
    title: "searchResultWifi",
    where: "searchWhereRoomInfo",
    tab: "room",
    target: "room-info",
    keywords: "wlan internet passwort wifi wi-fi password",
  },
  {
    title: "searchResultHouseRules",
    where: "searchWhereRoomInfo",
    tab: "room",
    target: "room-info",
    keywords:
      "hausordnung ruhezeiten regeln gäste house rules quiet hours guests",
  },
  {
    title: "searchResultWasteSorting",
    where: "searchWhereRoomInfo",
    tab: "room",
    target: "room-waste-sorting",
    keywords:
      "müll mülltrennung tonne trennung abfall waste sorting bin recycling trash",
  },
  {
    title: "searchResultLandlordConfirmation",
    where: "searchWhereRoomInfo",
    tab: "room",
    target: "room-landlord-confirmation",
    keywords:
      "anmeldung bürgeramt wohnungsgeber wohnungsgeberbestätigung meldebescheinigung registration landlord confirmation",
  },
  {
    title: "searchResultBroadcastingFee",
    where: "searchWhereRoomInfo",
    tab: "room",
    target: "room-broadcasting-fee",
    keywords:
      "rundfunk rundfunkbeitrag gez beitragsservice broadcasting fee tv",
  },
  {
    title: "searchResultReportIssue",
    where: "searchWhereHelp",
    tab: "help",
    target: "help-request",
    keywords:
      "kaputt reparatur ticket problem melden heizung broken repair issue report heating",
  },
  {
    title: "searchResultCleaningRota",
    where: "searchWhereCommunity",
    tab: "community",
    keywords: "putzen putzplan reinigung cleaning rota",
  },
  {
    title: "searchResultFlatChat",
    where: "searchWhereCommunity",
    tab: "community",
    keywords: "chat wg-chat nachricht mitbewohner message flatmates flat",
  },
  {
    title: "searchResultSharedCosts",
    where: "searchWhereCommunity",
    tab: "community",
    keywords:
      "kosten gemeinsame splitwise geld einkauf shared costs money shopping expenses",
  },
  {
    title: "searchResultReferralCode",
    where: "searchWhereReferrals",
    tab: "referrals",
    keywords:
      "freunde werben empfehlung empfehlungscode code rabatt refer referral friends discount",
  },
];

/** An applicant with no booking yet. */
export const APPLICANT_SEARCH_INDEX: readonly SearchEntry[] = [
  {
    title: "searchResultUploadDocuments",
    where: "searchWhereApplicationsDocuments",
    tab: "applications",
    target: "applications-documents",
    keywords:
      "unterlagen hochladen dokumente profil ausweis schufa upload documents profile id",
  },
  {
    title: "searchResultProofOfIncome",
    where: "searchWhereApplicationsDocuments",
    tab: "applications",
    target: "applications-documents",
    keywords:
      "gehalt einkommen einkommensnachweis bürgschaft immatrikulation schufa salary income proof guarantor enrolment",
  },
  {
    title: "searchResultApplicationStatus",
    where: "searchWhereApplications",
    tab: "applications",
    keywords:
      "status bewerbung bewerbungsstatus prüfung zusage application review offer",
  },
  {
    title: "searchResultSavedRooms",
    where: "searchWhereSaved",
    tab: "saved",
    keywords: "favoriten merken gemerkt gemerkte zimmer saved favourites rooms",
  },
  {
    title: "searchResultRequestViewing",
    where: "searchWhereHelp",
    tab: "help",
    target: "help-request",
    keywords: "besichtigung anfragen termin video viewing request appointment",
  },
  {
    title: "searchResultWhatsIncluded",
    where: "searchWhereHelpFaq",
    tab: "help",
    target: "help-faq",
    keywords:
      "miete nebenkosten internet möbel warm enthalten rent utilities furniture included",
  },
  {
    // The applicant FAQ has no deposit question, so this lands on the
    // contact card, where it can be asked.
    title: "searchResultDeposit",
    where: "searchWhereHelp",
    tab: "help",
    target: "help-request",
    keywords: "kaution deposit",
  },
  {
    title: "searchResultMultipleApplications",
    where: "searchWhereHelpFaq",
    tab: "help",
    target: "help-faq",
    keywords:
      "mehrere bewerben bewerbungen zurückziehen multiple apply applications withdraw",
  },
];

const RESULT_LIMIT = 6;

/*
  Both literal titles are always searchable, whatever the reader's language:
  a German reader who types the English "login" still finds "WLAN-Zugang",
  and the other way round, without every title word being copied into the
  keyword bag by hand.
*/
const EN_LABELS = getTenantDashboardV2Labels("en");
const DE_LABELS = getTenantDashboardV2Labels("de");

/**
 * Fold text for matching: lowercase, with the German letters spelled the
 * way people type them without an umlaut key, so "bestaetigung" finds
 * "Bestätigung". The plain-vowel spelling is added too ("bestatigung").
 */
function fold(text: string): string {
  const lower = text.toLowerCase();
  const spelled = lower
    .replace(/ß/g, "ss")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue");
  const plain = lower.replace(/ß/g, "ss").normalize("NFD").replace(/[̀-ͯ]/g, "");
  return spelled === plain ? spelled : `${spelled} ${plain}`;
}

/**
 * Match `query` against an index and return what to show, capped at
 * {@link RESULT_LIMIT}.
 *
 * Every word of the query must appear somewhere in the result's title (in
 * English, German or the reader's language) or keywords, in any order — so "confirmation
 * landlord" and "contract pdf" match, where a whole-phrase substring did not.
 *
 * An empty or whitespace-only query returns no results — the dropdown stays
 * closed rather than dumping the whole index on focus. `availableTabs`
 * drops results on a tab this tenant cannot open yet (Community before the
 * tenancy starts): selecting one would fall back to the overview and look
 * like the click did nothing.
 */
export function searchTenantDashboard(
  query: string,
  index: readonly SearchEntry[],
  labels: Pick<DashboardV2Labels, SearchCopyKey>,
  availableTabs?: readonly DashboardV2Tab[]
): SearchEntry[] {
  const words = fold(query)
    .split(/[\s,.;:()/]+/)
    .filter(Boolean);
  if (words.length === 0) return [];
  return index
    .filter((entry) => !availableTabs || availableTabs.includes(entry.tab))
    .filter((entry) => {
      const haystack = fold(
        [
          labels[entry.title],
          EN_LABELS[entry.title],
          DE_LABELS[entry.title],
          entry.keywords,
        ].join(" ")
      );
      return words.every((word) => haystack.includes(word));
    })
    .slice(0, RESULT_LIMIT);
}
